import { useMemo } from 'react'
import { Budget, Customer, HoardOrder, SaleOrder, useSaleOperationHooks } from '../../../sales'
import { PaymentStatus } from '../../../operations'
import { DeliveryStatus } from '../../../deliveries'
import { getMonthRange, isInRange, isToday, daysAgoStr, toLocalDateStr } from '../../utils/dashboardUtils'
import { useOperationTotals } from '../useOperationTotals'

export type SalesKpis = {
    salesMonth: number
    salesPrevMonth: number
    paymentsMonth: number
    paymentsPrevMonth: number
    budgetsMonth: number
    pendingCollections: number
    hasOverdue: boolean
    salesToday: number
    salesTodayCount: number
    salesSameDayLastWeek: number
    hoardsToday: number
    hoardsTodayCount: number
    paymentsToday: number
    undeliveredSalesCount: number
}

// Sales indicators shared by several cards of the dashboard (calculated once)
export const useSalesKpis = (
    sales: SaleOrder[],
    hoards: HoardOrder[],
    budgets: Budget[],
    customers: Customer[]
): SalesKpis => {
    const { getSaleTotal, getHoardTotal } = useOperationTotals()
    const { getCustomersDebt } = useSaleOperationHooks()

    return useMemo(() => {
        const currRange = getMonthRange(0)
        const prevRange = getMonthRange(1)
        const sumSales = (list: SaleOrder[]) => list.reduce((sum, sale) => sum + getSaleTotal(sale), 0)
        const sumHoards = (list: HoardOrder[]) => list.reduce((sum, hoard) => sum + getHoardTotal(hoard), 0)
        const sumPayments = (range: { start: string, end: string, label: string }) => [...sales, ...hoards].reduce((sum, operation) =>
            sum + operation.paymentItems
                .filter(paymentItem => isInRange(paymentItem.paymentDate, range))
                .reduce((itemsSum, paymentItem) => itemsSum + (Number(paymentItem.amount) || 0), 0)
        , 0)

        const salesMonth = sumSales(sales.filter(sale => isInRange(sale.creationDate, currRange))) +
            sumHoards(hoards.filter(hoard => isInRange(hoard.creationDate, currRange)))
        const salesPrevMonth = sumSales(sales.filter(sale => isInRange(sale.creationDate, prevRange))) +
            sumHoards(hoards.filter(hoard => isInRange(hoard.creationDate, prevRange)))

        // Same calculation as the top debtors card and the debtors list
        const { customersDebt } = getCustomersDebt([...sales, ...hoards], customers)
        const pendingCollections = customersDebt.reduce((sum, customerDebt) => sum + customerDebt.totalDebt, 0)

        const salesTodayList = sales.filter(sale => isToday(sale.creationDate))
        const hoardsTodayList = hoards.filter(hoard => isToday(hoard.creationDate))
        const sameDayLastWeek = daysAgoStr(7)

        return {
            salesMonth,
            salesPrevMonth,
            paymentsMonth: sumPayments(currRange),
            paymentsPrevMonth: sumPayments(prevRange),
            budgetsMonth: budgets.filter(budget => isInRange(budget.date, currRange)).length,
            pendingCollections,
            hasOverdue: sales.some(sale => sale.paymentStatus !== PaymentStatus.PAYED && sale.latePayment),
            salesToday: sumSales(salesTodayList),
            salesTodayCount: salesTodayList.length,
            salesSameDayLastWeek: sumSales(sales.filter(sale => toLocalDateStr(sale.creationDate) === sameDayLastWeek)),
            hoardsToday: sumHoards(hoardsTodayList),
            hoardsTodayCount: hoardsTodayList.length,
            paymentsToday: [...sales, ...hoards].reduce((sum, operation) =>
                sum + operation.paymentItems
                    .filter(paymentItem => isToday(paymentItem.paymentDate))
                    .reduce((itemsSum, paymentItem) => itemsSum + (Number(paymentItem.amount) || 0), 0)
            , 0),
            undeliveredSalesCount: sales.filter(sale => sale.deliveryStatus !== DeliveryStatus.DELIVERED).length
        }
    }, [sales, hoards, budgets, customers])
}
